import { useCallback, useRef } from 'react';
import { DefaultErrorHandlersProvider } from '@antimatterhq/ui/contexts/DefaultErrorHandlerContext';
import { DefaultSuccessHandlersProvider } from '@antimatterhq/ui/contexts/DefaultSuccessHandlerContext';
import { AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { isNetworkError } from '@antimatterhq/ui/utils/isNetworkError';
import { networkStatusAtom } from '@/atoms/networkStatus/networkStatusAtoms';
import { useStandaloneAuthentication } from '@/hooks/authentication/useStandaloneAuthentication';

const useStandaloneDefaultHandlers = () => {
  const { refreshAuthentication } = useStandaloneAuthentication();

  // Debounce logic identical to the client app (see rationale there)
  const DEFAULT_ONLINE_DEBOUNCE_MS = 1500;
  const onlineTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const setNetworkStatusOffline = useAtomCallback(
    useCallback((_, set) => {
      set(networkStatusAtom, 'offline');
    }, []),
  );

  const setNetworkStatusOnline = useAtomCallback(
    useCallback((_, set) => {
      set(networkStatusAtom, 'online');
    }, []),
  );

  const defaultAxiosErrorHandler = useCallback(
    async (error: AxiosError) => {
      if (onlineTimeoutRef.current) {
        clearTimeout(onlineTimeoutRef.current);
        onlineTimeoutRef.current = undefined;
      }
      // Handle 401 errors
      if (error.response?.status === 401) {
        await refreshAuthentication();
        return true;
      }

      // Network errors
      if (isNetworkError(error)) {
        setNetworkStatusOffline();

        // Mark as unhandled so react-query's retry mechanism takes over.
        return false;
      }

      return false;
    },
    [refreshAuthentication, setNetworkStatusOffline],
  );

  const defaultSuccessHandler = useCallback(async () => {
    if (onlineTimeoutRef.current) clearTimeout(onlineTimeoutRef.current);

    onlineTimeoutRef.current = setTimeout(() => {
      setNetworkStatusOnline();
      onlineTimeoutRef.current = undefined;
    }, DEFAULT_ONLINE_DEBOUNCE_MS);
  }, [setNetworkStatusOnline]);

  return { defaultAxiosErrorHandler, defaultSuccessHandler };
};

export const StandaloneRequestHandlersProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { defaultAxiosErrorHandler, defaultSuccessHandler } =
    useStandaloneDefaultHandlers();

  return (
    <DefaultErrorHandlersProvider
      defaultAxiosErrorHandler={defaultAxiosErrorHandler}
    >
      <DefaultSuccessHandlersProvider
        defaultSuccessHandler={defaultSuccessHandler}
      >
        {children}
      </DefaultSuccessHandlersProvider>
    </DefaultErrorHandlersProvider>
  );
};
